import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import BlocksContents from '../Template/BlocksContents';
import Template from '../../../models/Template/Template';
import BlockData from '../../../models/BlockData';
import ModalComponent from '../../../components/ModalComponent';
import AtomicBlockSelection from './AtomicBlockSelectionModal';
import { CategoriesContext } from '../../../contexts/CategoriesContext';
import ImageSelectionModal from './ImageSelectionModal';
import { ImageItem, ImagesContext } from '../../../contexts/ImagesContext';
import Category from '../../../models/Category/Category';
import BlockType from '../../../models/BlockType';
import describe = BlockType.describe;
import { getTemplate } from '../../../services/template';

interface Props {
  open: boolean
  template: Template | null
  defaultCategoryId: string
  handleClose: () => void
  handleSubmit: (template: Template) => void
}

function BlockModal(props: Props) {
  const {
    open, handleClose, handleSubmit, template, defaultCategoryId,
  } = props;

  const { requestAllCategory } = useContext(CategoriesContext);
  const { exchangeForUrl } = useContext(ImagesContext);

  const [categories, setCategories] = useState<Category[]>([]);

  // ベースブロックモーダルをコントロールするフラグ
  const [systemBlocksModalOpen, setSystemBlocksModalOpen] = useState(false);

  // ベースブロックを選んだら、最終確認へ
  const [blockDataToBeConfirmed, setBlockDataToBeConfirmed] = useState<BlockData | null>(null);

  // 一覧の編集ボランから入ってきた場合は、blockIdがつきます。
  const [blockId, setBlockId] = useState('');
  const [blockTitle, setBlockTitle] = useState('');
  const [blockDesc, setBlockDesc] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [systemBlockName, setSystemBlockName] = useState('');

  // プレビューためのblockData
  const [blockData, setBlockData] = useState<BlockData | null>(null);

  const [imageSelectionModalOpen, setImageSelectionModalOpen] = useState(false);

  const [selectedImageItem, setSelectedImageItem] = useState<ImageItem>(null);

  // 必須項目がない場合は、警告表示
  const lackInfoError = useMemo(() => {
    if (blockTitle == null || blockTitle.trim().length === 0) {
      return true;
    }

    if (categoryId == null || categoryId.trim().length === 0) {
      return true;
    }

    if (blockData == null) {
      return true;
    }

    return false;
  }, [blockTitle, blockData, categoryId]);

  useEffect(() => {
    if (template?.id) {
      getTemplate(template.id).then((response) => {
        setBlockData(response.data.data.template);
      });
    }
  }, [template]);

  useEffect(() => {
    requestAllCategory().then((r) => setCategories(r));
  }, [requestAllCategory]);

  useEffect(() => {
    setSystemBlockName(describe(blockData?.type));
  }, [blockData]);

  useEffect(() => {
    if (open) {
      return;
    }
    // モーダルが閉じられた時に
    setBlockDataToBeConfirmed(null);

    setBlockId('');
    setBlockTitle('');
    setBlockDesc('');
    setBlockData(null);
    setCategoryId('');
    setSystemBlockName('');

    // ブロックイメージをモーダルが閉じたときにリセットします。
    setSelectedImageItem(null);
  }, [open, template]);

  // 編集から入ってきた場合には
  useEffect(() => {
    if (template == null) {
      return;
    }
    setBlockId(template.id);
    setBlockTitle(template.title);
    setBlockDesc(template.description || '');
    // deepcopyして、上層のtemplateとunbindします。
    setBlockData(JSON.parse(JSON.stringify(template.template)));
    setCategoryId(template.categoryId);

    // for security concern, exchange for a safe url
    exchangeForUrl(template.img_url).then((url) => {
      setSelectedImageItem({
        id: template.img_id,
        url,
        thumbnail: template.thumbnail,
      });
    });
  }, [template, exchangeForUrl]);

  const handleImageChange = (image: ImageItem) => {
    exchangeForUrl(image.url).then((url) => {
      setSelectedImageItem({
        id: image.id,
        url,
        thumbnail: image.thumbnail,
      });
    });
  };

  // デフォルトのカテゴリーoptionを設定する
  useEffect(() => {
    setCategoryId(defaultCategoryId);
  }, [defaultCategoryId]);

  // ベースブロックモーダルから戻ってきた場合
  const handleTemplateSelectedFromSystemBlockModal = (blockData: BlockData) => {
    // deepcopyします
    setBlockData(JSON.parse(JSON.stringify(blockData)));
  };

  // 修正また新規作成のリクエストをなげます
  const handleSave = () => {
    if (blockTitle == null || blockTitle.trim().length === 0) {
      return;
    }

    if (categoryId.trim().length === 0) {
      return;
    }

    if (blockData == null) {
      return;
    }
    const data: Template = {
      id: blockId,
      categoryId,
      title: blockTitle,
      description: blockDesc,
      template: blockData,
      type: 'system-block',
      img_id: selectedImageItem && selectedImageItem.id,
      thumbnail: selectedImageItem && selectedImageItem.thumbnail,
      source_template_name: systemBlockName,
    };
    handleSubmit(data);
  };

  const title = useMemo(() => {
    if (template == null) {
      return 'ブロック作成';
    }

    return 'ブロック編集';
  }, [template]);

  return (
      <>
        <ModalComponent
          loading={false}
          errorMessage=""
          size="xl"
          title={title}
          confirmButtonText="保存する"
          cancelButtonText="閉じる"
          onConfirm={handleSave}
          show={open}
          closeModal={() => {
            handleClose();
          }}
          centered
          animation={false}
          isLackOfInfo={lackInfoError}
          scrollable
          // contentClassName="fullViewportHeight"
          style={{
            zIndex: 1050,
          }}
        >
          <div className=" m-3">
            <form className="d-flex flex-column h-100">
              <div className="row">
                <div className="col-sm-8">
                  <div className="row g-3 mb-3">
                    <label className="col-form-label col-sm-3 text-sm-start">
                      ブロック名
                      <span>*</span>
                    </label>
                    <div className="col-sm-9">
                      <div className="is-invalid" id="validatione1">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="ブロック名を入力"
                          value={blockTitle}
                          onChange={(e) => setBlockTitle(e.target.value)}
                        />
                      </div>
                      <div
                        className="invalid-feedback d-none"
                        id="validationeFeedback1"
                      >
                        ブロック名を入力してください。
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <label className="col-form-label col-sm-3 text-sm-start">
                      ブロック説明
                    </label>
                    <div className="col-sm-9">
                      <div className="is-invalid" id="validatione2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="ブロック説明を入力"
                          value={blockDesc}
                          onChange={(e) => setBlockDesc(e.target.value)}
                        />
                      </div>
                      <div
                        className="invalid-feedback d-none"
                        id="validationeFeedback2"
                      >
                        ブロック説明を入力してください。
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <label className="col-form-label col-sm-3 text-sm-start">
                      カテゴリ
                      <span>*</span>
                    </label>
                    <div className="col-sm-9">
                      <div className="is-invalid" id="validatione3">
                        <select
                          className="form-select"
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                        >
                          <option value="">未選択</option>
                          {categories.map((c) => (<option key={c.id} value={c.id}>{c.title}</option>))}
                        </select>
                      </div>
                      <div
                        className="invalid-feedback d-none"
                        id="validationeFeedback3"
                      >
                        カテゴリを選択してください。
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <label className="col-form-label col-sm-3 text-sm-start">
                      ベースブロック
                      <span>*</span>
                    </label>
                    <div className="col-sm-9">
                      <div className="is-invalid" id="validatione4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            value={systemBlockName}
                            placeholder="ベースブロック名"
                            aria-describedby="button-baseblock"
                            readOnly
                          />
                          <button
                            className="btn btn-primary"
                            type="button"
                            id="button-baseblock"
                            onClick={() => setSystemBlocksModalOpen(true)}
                          >
                            ベースブロック選択 ...
                          </button>
                        </div>
                      </div>
                      <div
                        className="invalid-feedback d-none"
                        id="validationeFeedback4"
                      >
                        ベースブロックを選択してください。
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="row g-3 mb-3 h-100">
                    <div className="col-12  d-flex flex-column">
                      <button
                        className="btn btn-primary"
                        type="button"
                        id="button-blockimage"
                        onClick={() => setImageSelectionModalOpen(true)}
                      >
                        イメージ画像 ...
                      </button>
                      <div className="block-image-view mt-2 flex-fill">
                        <div className="block-image">
                          <img src={selectedImageItem?.url || '/img/blocks/sample_block.png'} alt="" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row g-3 pb-3 flex-fill">
                <div className="col-12">
                  <div className="h-100">
                    {/* previewの部分 */}
                    <BlocksContents
                      blockData={blockData}
                      handleDataChange={(data) => {
                        setBlockData(data);
                      }}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </ModalComponent>
        <AtomicBlockSelection
          open={systemBlocksModalOpen}
          handleClose={() => {
            setSystemBlocksModalOpen(false);
          }}
          handleSave={(b) => {
            if (blockData == null) {
              setBlockData(b);
              setSystemBlocksModalOpen(false);
              return;
            }
            setBlockDataToBeConfirmed(b);
            setSystemBlocksModalOpen(false);
          }}
        />
        <ModalComponent
          noheader
          size="sm"
          backdrop="static"
          backdropClassName="z-index-2000"
          style={{ zIndex: 2001 }}
          keyboard={false}
          confirmButtonText="はい"
          confirmButtonColor="success"
          onConfirm={() => {
            handleTemplateSelectedFromSystemBlockModal(blockDataToBeConfirmed);
            setBlockDataToBeConfirmed(null);
          }}
          show={blockDataToBeConfirmed != null}
          closeModal={() => {
            setBlockDataToBeConfirmed(null);
          }}
          centered
          animation={false}
        >
          <div>
            <div>編集内容が破棄されます。</div>
            <div>よろしいですか？</div>
          </div>
        </ModalComponent>
        <ImageSelectionModal
          open={imageSelectionModalOpen}
          handleClose={() => setImageSelectionModalOpen(false)}
          handleSave={(item: ImageItem) => {
            handleImageChange(item);
            setImageSelectionModalOpen(false);
          }}
        />
      </>
  );
}

export default BlockModal;
